import React from 'react';
import { Box, xcss, Text, Pressable } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Lozenge from '@atlaskit/lozenge';
import { useIntl } from '@atlassian/jira-intl';
import { usePreferencesContext } from '@atlassian/jsm-enterprise-reports-common';
import { useRouterActions } from '@atlassian/react-resource-router';
import { ENTRY_POINT_ID_JSM_ASSETS_REPORTING } from '@atlassian/jira-feedback-collector/src/constants.tsx';
import { useFeedbackCollectorActions } from '@atlassian/jira-feedback-collector/src/controllers/index.tsx';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { isNewReportsNavExperienceEnabled } from '../../feature-gate-helpers.tsx';
import { messages } from './messages.tsx';

export const PreferenceBanner = (): React.JSX.Element | null => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { updatePreferences } = usePreferencesContext();
	const { formatMessage } = useIntl();
	const { replace } = useRouterActions();
	const { openFeedbackCollector } = useFeedbackCollectorActions();
	const updatePreferencesAndRedirect = () => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'toggled',
			actionSubject: 'reportsNewExperienceButton',
		});
		fireTrackAnalytics(analyticsEvent, 'reportsNewExperienceButton toggled', {
			source: 'In-product AVP Assets Reports',
			toggleState: 'Disabled',
		});

		updatePreferences(false, () => {
			replace(`/jira/servicedesk/assets/reports?t=${new Date().getTime()}`);
		});
	};

	const openAssetsReportsFeedbackCollector = () => {
		openFeedbackCollector({
			entrypointId: ENTRY_POINT_ID_JSM_ASSETS_REPORTING,
		});
	};

	if (isNewReportsNavExperienceEnabled()) {
		return null;
	}

	return (
		<Box xcss={preferenceBannerWrapper}>
			<Lozenge appearance="moved" isBold>
				{formatMessage(messages.eapLozengeTitle)}
			</Lozenge>
			&nbsp;
			<Text>
				{formatMessage(messages.bannerTitle, {
					feedbackLink: (chunks: React.ReactNode[]) => (
						<Pressable xcss={linkStyles} onClick={openAssetsReportsFeedbackCollector}>
							{chunks}
						</Pressable>
					),
					previousExperienceLink: (chunks: React.ReactNode[]) => (
						<Pressable xcss={linkStyles} onClick={updatePreferencesAndRedirect}>
							{chunks}
						</Pressable>
					),
				})}
			</Text>
		</Box>
	);
};

const preferenceBannerWrapper = xcss({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	height: '100%',
	width: '100%',
	paddingTop: 'space.075',
	paddingBottom: 'space.075',
	paddingLeft: 'space.150',
	paddingRight: 'space.150',
	borderBottomWidth: 'border.width',
	borderBottomStyle: 'solid',
	borderBottomColor: token('color.border'),
	backgroundColor: 'elevation.surface.sunken',
});

const linkStyles = xcss({
	color: 'color.link',
	background: 'none',
	padding: '0',
	margin: '0',
	':hover': {
		textDecoration: 'underline',
	},
});
